import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatMiniFabButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, EMPTY, filter, Subscription, switchMap } from 'rxjs';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { WorkInstructionEditorComponent } from '../../../settings/work-instructions/work-instruction-editor/work-instruction-editor.component';
import { WorkInstructionStepperComponent } from '../../work-instruction-stepper/work-instruction-stepper.component';
import { IWorkInstructionStepperParams } from '../../work-instruction-stepper/work-instruction-stepper.interface';
import { ApplyChangesRecursivelyQuestionModalComponent } from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { RecurringItemService } from '../apply-changes-recursively-question-modal/recurring-item.service';
import { IWorkInstructionItemCard } from '../item-work-instruction-list/item-work-instruction-list.interface';
import { IWorkInstructions } from './link-work-instruction.interface';
import { LinkWorkInstructionStore } from './link-work-instruction.store';

@Component({
  imports: [ReactiveFormsModule, TranslateModule, MatMiniFabButton, MatIcon],
  providers: [LinkWorkInstructionStore, RecurringItemService],
  selector: 'app-link-work-instruction',
  styleUrl: './link-work-instruction.component.scss',
  templateUrl: './link-work-instruction.component.html',
})
export class LinkWorkInstructionComponent implements OnInit, OnDestroy, OnChanges {
  @Input() boardId?: number;
  @Input() itemId?: number;
  @Input() dueDateForRecurrence?: string | null;

  @Input() public set loadTrigger(value: boolean) {
    if (value && this.boardId && this.itemId) {
      this.loadWorkInstructions(this.boardId, this.itemId);
    }
  }

  private isEditModal = false;
  public linkedWorkInstructions: IWorkInstructions[] = [];
  protected readonly subscriptions: Subscription[] = [];

  constructor(
    public readonly store: LinkWorkInstructionStore,
    public readonly dialogService: MatDialog,
    private readonly translate: TranslateService,
    private readonly recurringItemService: RecurringItemService,
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this.store
        .select((state) => state.assignmentLoading)
        .pipe(
          distinctUntilChanged(),
          filter((status) => status === ELoadStatus.success),
        )
        .subscribe(() => {
          if (this.boardId && this.itemId) {
            this.loadWorkInstructions(this.boardId, this.itemId);
          }
        }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.isEditModal = Boolean(this.itemId);

    const boardIdChanged = changes['boardId']?.currentValue !== changes['boardId']?.previousValue;
    const itemIdChanged = changes['itemId']?.currentValue !== changes['itemId']?.previousValue;

    if ((boardIdChanged || itemIdChanged) && this.boardId !== undefined && this.itemId !== undefined) {
      this.loadWorkInstructions(this.boardId, this.itemId);
    }
  }

  private loadWorkInstructions(boardId: number, itemId: number): void {
    this.store.loadWorkInstructions({ boardId, itemId });
  }

  public onRemoveLinkClick(workInstruction: IWorkInstructionItemCard): void {
    if (Boolean(this.dueDateForRecurrence) && workInstruction.assignmentId) {
      this.askApplyChangesRecursively(workInstruction.assignmentId);

      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('page.items.delete.unassignWorkInstruction', {
          context: workInstruction.name,
        }),
        submitButtonText: this.translate.instant('button.unassign'),
        title: this.translate.instant('system.unassign.confirmationTitle', {
          context: this.translate.instant('field.workInstruction'),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (!result) {
              return EMPTY;
            }

            if (!this.isEditModal) {
              const index: number = this.linkedWorkInstructions.findIndex((link) => {
                return link.id === workInstruction.id;
              });

              if (index !== -1) {
                this.linkedWorkInstructions.splice(index, 1);
              }

              return EMPTY;
            }

            if (workInstruction.assignmentId) {
              this.unassignItemWorkInstruction(workInstruction.assignmentId);
            }

            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  public onClickedOpenStepper(workInstructionId: number): void {
    const workInstruction: IWorkInstructionItemCard | undefined = this.store.data$().find((item: IWorkInstructionItemCard) => item.id === workInstructionId);

    if (!workInstruction) {
      return;
    }

    this.dialogService.open(WorkInstructionStepperComponent, {
      ...WorkInstructionEditorComponent.dialogConfig,
      data: {
        boardId: this.boardId,
        dueDateForRecurrence: this.dueDateForRecurrence,
        itemGuideAssignmentId: workInstruction.assignmentId,
        itemId: this.itemId,
        name: workInstruction.name,
        workInstructionId,
      } as IWorkInstructionStepperParams,
    });
  }

  public ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  private unassignItemWorkInstruction(
    id: number,
    recurrenceType: EItemRecurrenceEditType = EItemRecurrenceEditType.onlyThis,
  ): void {
    this.store.unassignWorkInstructions({
      id,
      ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
        recurrenceType,
        this.dueDateForRecurrence ?? null,
      ),
    });
  }

  private askApplyChangesRecursively(assignmentId: number): void {
    this.subscriptions.push(
      this.dialogService
        .open(ApplyChangesRecursivelyQuestionModalComponent, {
          data: { mode: ERecursiveAction.edit },
        })
        .afterClosed()
        .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType | '' | undefined): void => {
          if (!selectedRecurrenceEditType) {
            return;
          }

          this.unassignItemWorkInstruction(assignmentId, selectedRecurrenceEditType);

          return;
        }),
    );
  }
}
